/**
 * Bill linked records strip — `prototype/purchases/bill-detail.html` ▸
 * `.linked-strip`. Three cells: PO, GRN, vendor payments. `.copy` uses
 * the 3-tier `.k`/`.v`/`.sub` text hierarchy. `.ic` uses state variants.
 */
import { Link2, FileText, FileCheck2, CreditCard } from "lucide-react";
import { formatDate, paiseToInrShort } from "../../../lib/format.ts";

/** References for bill-adjacent linked-record placeholders. */
export interface BillLinkedStripProps {
  vendorDocNumber: string | null;
  payments: readonly BillLinkedPayment[];
  paymentsLoading?: boolean;
}

interface BillLinkedPayment {
  grossPaise: string;
  mode: string;
  paidOn: string;
  reference: string | null;
  status: string;
}

/** Render compact linked records for purchase-order, GRN, and payment gaps. */
export function BillLinkedStrip({
  vendorDocNumber,
  payments,
  paymentsLoading = false,
}: BillLinkedStripProps) {
  const cellCount = 3;
  const paidPayments = payments.filter((payment) => payment.status === "paid");
  const latestPaidPayment = paidPayments[0] ?? null;
  const paidPaise = paidPayments.reduce(
    (total, payment) => total + BigInt(payment.grossPaise),
    0n,
  );
  const paymentState = paymentCopy({
    count: paidPayments.length,
    latestPaidPayment,
    paidPaise,
    paymentsLoading,
    vendorDocNumber,
  });

  return (
    <section className="linked-strip" aria-label="Linked records">
      <div className="head">
        <Link2 className="ic-h" size={18} aria-hidden />
        <h3>Linked records · {cellCount}</h3>
      </div>
      <div className="grid">
        <a className="linked-cell" href="#">
          <span className="ic muted">
            <FileText size={14} />
          </span>
          <div className="copy">
            <div className="k">Purchase order</div>
            <div className="v">Not linked</div>
            <div className="sub">PO entity lands in V1.x — capture from vendor</div>
          </div>
        </a>
        <a className="linked-cell" href="#">
          <span className="ic muted">
            <FileCheck2 size={14} />
          </span>
          <div className="copy">
            <div className="k">Goods receipt</div>
            <div className="v">Not linked</div>
            <div className="sub">GRN entity lands in V1.x</div>
          </div>
        </a>
        <a className="linked-cell" href="#">
          <span className={`ic ${paidPayments.length > 0 ? "ok" : "muted"}`}>
            <CreditCard size={14} />
          </span>
          <div className="copy">
            <div className="k">Vendor payments · {paymentState.countLabel}</div>
            <div className="v">{paymentState.valueLabel}</div>
            <div className="sub">{paymentState.subLabel}</div>
          </div>
        </a>
      </div>
    </section>
  );
}

function paymentCopy(input: {
  count: number;
  latestPaidPayment: BillLinkedPayment | null;
  paidPaise: bigint;
  paymentsLoading: boolean;
  vendorDocNumber: string | null;
}): { countLabel: string; valueLabel: string; subLabel: string } {
  if (input.paymentsLoading && input.count === 0) {
    return {
      countLabel: "checking",
      valueLabel: "Checking payments",
      subLabel: input.vendorDocNumber
        ? `Bill ref: ${input.vendorDocNumber}`
        : "Record after founder approval",
    };
  }

  if (input.count === 0 || input.latestPaidPayment === null) {
    return {
      countLabel: "0",
      valueLabel: "Not paid yet",
      subLabel: input.vendorDocNumber
        ? `Bill ref: ${input.vendorDocNumber}`
        : "Record after founder approval",
    };
  }

  return {
    countLabel: input.count.toString(),
    valueLabel: `Paid ${paiseToInrShort(input.paidPaise)}`,
    subLabel: paymentSubLabel(input.latestPaidPayment),
  };
}

function paymentSubLabel(payment: BillLinkedPayment): string {
  if (payment.reference) return `Ref: ${payment.reference}`;
  return `${payment.mode.toUpperCase()} · ${formatDate(payment.paidOn)}`;
}
